import {
  SEAT_CLASSES,
  SEAT_CLASS_SPECS,
  type AircraftType,
  type CabinConfiguration,
  type CabinWeightBreakdown,
  type ClassMap,
  type ClassRatios,
  type SeatClass,
} from '../models';
import {
  COMFORT_PROFILES,
  DENSITY_COMFORT_SPAN,
  DENSITY_COMFORT_START_RATIO,
  FIXED_PITCH_COMFORT,
  FUEL_WEIGHT_PENALTY_FACTOR,
  GALLEY_FREE_SEAT_LIMIT,
  GALLEY_WEIGHT_KG,
  IN_SEAT_POWER_WEIGHT_KG_PER_SEAT,
  MIN_FUEL_BURN_FRACTION,
  REFERENCE_CABIN_KG_PER_FLOOR_UNIT,
  SEAT_WEIGHT_KG,
  SERVICE_LOAD_PER_GALLEY,
  SERVICE_LOAD_PER_SEAT,
  TOLERANCE_MAX_MULTIPLIER,
  TOLERANCE_MIN_MULTIPLIER,
  TOLERANCE_PROFILES,
  WIFI_HARDWARE_WEIGHT_KG,
} from './cabinConstants';

/**
 * Pure cabin calculations. Every function tolerates malformed input (NaN,
 * negatives) by sanitising it, so metrics can always be displayed while the
 * validator reports what is wrong.
 */

/* -------------------------------------------------------------------------- *
 * Small utilities
 * -------------------------------------------------------------------------- */

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Non-negative finite number; anything else becomes 0. */
export function sanitizeCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/** Builds a ClassMap by evaluating `build` once per seat class. */
export function buildClassMap<T>(build: (seatClass: SeatClass) => T): ClassMap<T> {
  return {
    ECONOMY: build('ECONOMY'),
    PREMIUM_ECONOMY: build('PREMIUM_ECONOMY'),
    BUSINESS: build('BUSINESS'),
    FIRST: build('FIRST'),
  };
}

function sumClassMap(map: ClassMap<number>): number {
  return SEAT_CLASSES.reduce((total, seatClass) => total + map[seatClass], 0);
}

/* -------------------------------------------------------------------------- *
 * Seats and floor space
 * -------------------------------------------------------------------------- */

export function getSeatsByClass(config: CabinConfiguration): ClassMap<number> {
  return {
    ECONOMY: sanitizeCount(config.economySeats),
    PREMIUM_ECONOMY: sanitizeCount(config.premiumEconomySeats),
    BUSINESS: sanitizeCount(config.businessSeats),
    FIRST: sanitizeCount(config.firstSeats),
  };
}

export function getTotalSeats(config: CabinConfiguration): number {
  return sumClassMap(getSeatsByClass(config));
}

/** Share of installed seats per class; all zeros when the cabin is empty. */
export function getClassRatios(seats: ClassMap<number>): ClassRatios {
  const total = sumClassMap(seats);
  return buildClassMap((seatClass) => (total > 0 ? seats[seatClass] / total : 0));
}

/**
 * Pitch in effect for a class. Economy and Business use the configured value,
 * clamped to the class range; Premium Economy and First use the fixed product
 * pitch.
 */
export function getEffectivePitchInches(
  seatClass: SeatClass,
  config: CabinConfiguration
): number {
  const spec = SEAT_CLASS_SPECS[seatClass];
  let configured: number;
  if (seatClass === 'ECONOMY') {
    configured = config.economyPitchInches;
  } else if (seatClass === 'BUSINESS') {
    configured = config.businessPitchInches;
  } else {
    return spec.defaultPitchInches;
  }
  return Number.isFinite(configured)
    ? clamp(configured, spec.minPitchInches, spec.maxPitchInches)
    : spec.defaultPitchInches;
}

/**
 * Floor units one seat consumes. The spec value (1.0 Economy, 3.5 Business…)
 * applies at the class's default pitch; Economy and Business scale linearly
 * with pitch because seat-row length is proportional to pitch.
 */
export function floorUnitsPerSeat(seatClass: SeatClass, pitchInches: number): number {
  const spec = SEAT_CLASS_SPECS[seatClass];
  const configurable = seatClass === 'ECONOMY' || seatClass === 'BUSINESS';
  return configurable
    ? spec.unitsPerSeat * (pitchInches / spec.defaultPitchInches)
    : spec.unitsPerSeat;
}

export function getFloorUnitsByClass(config: CabinConfiguration): ClassMap<number> {
  const seats = getSeatsByClass(config);
  return buildClassMap(
    (seatClass) =>
      seats[seatClass] *
      floorUnitsPerSeat(seatClass, getEffectivePitchInches(seatClass, config))
  );
}

export function getFloorUnitsUsed(config: CabinConfiguration): number {
  return sumClassMap(getFloorUnitsByClass(config));
}

/* -------------------------------------------------------------------------- *
 * Weight and fuel
 * -------------------------------------------------------------------------- */

export function calculateCabinWeight(config: CabinConfiguration): CabinWeightBreakdown {
  const seats = getSeatsByClass(config);
  const seatsKg = SEAT_CLASSES.reduce(
    (total, seatClass) => total + seats[seatClass] * SEAT_WEIGHT_KG[seatClass],
    0
  );
  const galleysKg = sanitizeCount(config.galleyCount) * GALLEY_WEIGHT_KG;
  const wifiKg = config.wifiEquipped ? WIFI_HARDWARE_WEIGHT_KG : 0;
  const powerKg = config.inSeatPower
    ? sumClassMap(seats) * IN_SEAT_POWER_WEIGHT_KG_PER_SEAT
    : 0;
  return {
    seatsKg,
    galleysKg,
    wifiKg,
    powerKg,
    totalKg: seatsKg + galleysKg + wifiKg + powerKg,
  };
}

/** Cabin weight that the type's `baseFuelBurnKgHr` is quoted against. */
export function calculateReferenceCabinWeightKg(type: AircraftType): number {
  return type.cabinFloorUnits * REFERENCE_CABIN_KG_PER_FLOOR_UNIT;
}

export interface FuelBurnResult {
  readonly weightDeltaKg: number;
  readonly effectiveFuelBurnKgHr: number;
  readonly fuelBurnDeltaKgHr: number;
}

/**
 * Effective burn = base burn + weight delta × fuel weight penalty factor,
 * where the delta is measured against the type's reference cabin.
 */
export function calculateFuelBurn(
  type: AircraftType,
  cabinWeightKg: number
): FuelBurnResult {
  const weightDeltaKg = cabinWeightKg - calculateReferenceCabinWeightKg(type);
  const unclamped = type.baseFuelBurnKgHr + weightDeltaKg * FUEL_WEIGHT_PENALTY_FACTOR;
  const effectiveFuelBurnKgHr = Math.max(
    type.baseFuelBurnKgHr * MIN_FUEL_BURN_FRACTION,
    unclamped
  );
  return {
    weightDeltaKg,
    effectiveFuelBurnKgHr,
    fuelBurnDeltaKgHr: effectiveFuelBurnKgHr - type.baseFuelBurnKgHr,
  };
}

/* -------------------------------------------------------------------------- *
 * Comfort and price tolerance
 * -------------------------------------------------------------------------- */

/** Pitch comfort 0–1: position of the pitch inside its class range. */
function normalizedPitch(seatClass: SeatClass, config: CabinConfiguration): number {
  if (seatClass !== 'ECONOMY' && seatClass !== 'BUSINESS') {
    return FIXED_PITCH_COMFORT;
  }
  const spec = SEAT_CLASS_SPECS[seatClass];
  const pitch = getEffectivePitchInches(seatClass, config);
  return (pitch - spec.minPitchInches) / (spec.maxPitchInches - spec.minPitchInches);
}

/**
 * Comfort score (0–100) of one class from pitch, cabin density and amenities.
 * `floorUsageRatio` is floor units used divided by the type's budget.
 */
export function calculateClassComfort(
  seatClass: SeatClass,
  config: CabinConfiguration,
  floorUsageRatio: number
): number {
  const profile = COMFORT_PROFILES[seatClass];
  const pitchScore = normalizedPitch(seatClass, config);
  const densityScore = clamp01(
    1 - (floorUsageRatio - DENSITY_COMFORT_START_RATIO) / DENSITY_COMFORT_SPAN
  );
  const amenityScore =
    (config.wifiEquipped ? profile.wifiShare : 0) +
    (config.inSeatPower ? 1 - profile.wifiShare : 0);

  return (
    100 *
    clamp01(
      profile.baseComfort +
        profile.pitchWeight * pitchScore +
        profile.densityWeight * densityScore +
        profile.amenityWeight * amenityScore
    )
  );
}

/** Comfort per class; a class with no seats scores 0. */
export function calculateComfortByClass(
  config: CabinConfiguration,
  floorUsageRatio: number
): ClassMap<number> {
  const seats = getSeatsByClass(config);
  return buildClassMap((seatClass) =>
    seats[seatClass] > 0 ? calculateClassComfort(seatClass, config, floorUsageRatio) : 0
  );
}

/** Price-premium tolerance multiplier for a class given its comfort score. */
export function calculatePriceTolerance(seatClass: SeatClass, comfortScore: number): number {
  const profile = TOLERANCE_PROFILES[seatClass];
  const raw = 1 + (profile.sensitivity * (comfortScore - profile.expectedComfort)) / 50;
  return clamp(raw, TOLERANCE_MIN_MULTIPLIER, TOLERANCE_MAX_MULTIPLIER);
}

/** Tolerance per class; a class with no seats is neutral (1.0). */
export function calculateToleranceByClass(
  comfortByClass: ClassMap<number>,
  seats: ClassMap<number>
): ClassMap<number> {
  return buildClassMap((seatClass) =>
    seats[seatClass] > 0 ? calculatePriceTolerance(seatClass, comfortByClass[seatClass]) : 1
  );
}

/** Average of a per-class value weighted by seats; `fallback` when no seats. */
export function seatWeightedAverage(
  values: ClassMap<number>,
  seats: ClassMap<number>,
  fallback: number
): number {
  const total = sumClassMap(seats);
  if (total <= 0) {
    return fallback;
  }
  const weighted = SEAT_CLASSES.reduce(
    (sum, seatClass) => sum + values[seatClass] * seats[seatClass],
    0
  );
  return weighted / total;
}

/* -------------------------------------------------------------------------- *
 * Crew and galleys
 * -------------------------------------------------------------------------- */

/** Cabin crew required to dispatch: the type minimum or one per N seats. */
export function calculateRequiredCabinCrew(type: AircraftType, totalSeats: number): number {
  if (totalSeats <= 0) {
    return 0;
  }
  return Math.max(
    type.crew.minCabinCrew,
    Math.ceil(totalSeats / type.crew.seatsPerCabinCrew)
  );
}

/** Galleys needed to cater the cabin; small cabins need none. */
export function calculateRecommendedGalleys(config: CabinConfiguration): number {
  const seats = getSeatsByClass(config);
  const totalSeats = sumClassMap(seats);
  if (totalSeats <= GALLEY_FREE_SEAT_LIMIT) {
    return 0;
  }
  const serviceLoad = SEAT_CLASSES.reduce(
    (load, seatClass) => load + seats[seatClass] * SERVICE_LOAD_PER_SEAT[seatClass],
    0
  );
  return Math.max(1, Math.ceil(serviceLoad / SERVICE_LOAD_PER_GALLEY));
}
